/**
 * English (US) Locale
 */

export default {
  common: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Information',
    search: 'Search',
    refresh: 'Refresh',
    download: 'Download',
    upload: 'Upload',
    export: 'Export',
    import: 'Import',
    settings: 'Settings',
    logout: 'Logout',
    login: 'Login',
    yes: 'Yes',
    no: 'No',
  },

  nav: {
    dashboard: 'Dashboard',
    devices: 'Devices',
    settings: 'Settings',
    logout: 'Logout',
    language: 'Language',
  },

  auth: {
    loginTitle: 'Login to System',
    username: 'Username',
    password: 'Password',
    customerCode: 'Customer Code',
    customerCodePlaceholder: 'Enter customer code',
    passwordPlaceholder: 'Enter password',
    loginButton: 'Login',
    loggingIn: 'Logging in...',
    loginSuccess: 'Login successful',
    loginFailed: 'Login failed',
    invalidCredentials: 'Invalid account or password',
    networkError: 'Cannot connect to server, please check network connection',
    unknownError: 'Login failed, please try again later',
    testAccount: 'Test Account: admin / admin123',
    platformName: 'Solar Monitoring System',
    version: 'SolarSDGs IoT Platform v1.0.0',
    copyright: '© 2025 SolarSDGs. All rights reserved.',
  },

  dashboard: {
    title: 'Solar Monitoring Dashboard',
    deviceInfo: 'Device Information',
    deviceId: 'Device ID',
    deviceName: 'Device Name',
    deviceType: 'Device Type',
    location: 'Location',
    status: 'Status',
    online: 'Connected',
    offline: 'Disconnected',
    lastUpdate: 'Last Update',
    dataCount: 'Data Count',
    records: 'records',
    dataInterval: 'Data Interval',
    seconds: 's',
    networkType: 'Network Type',
    unknown: 'Unknown',

    // Power Data
    powerData: 'Power Data',
    generation: 'Generation',
    loadA: 'Load A',
    loadP: 'Load P',
    unit: {
      watt: 'mW',
      percent: '%',
    },

    // Efficiency Data
    efficiency: 'Efficiency Data',
    pagEfficiency: 'Load A Efficiency',
    pgpEfficiency: 'Load P Efficiency',

    // GPS Map
    map: {
      title: 'Device Location',
      latitude: 'Latitude',
      longitude: 'Longitude',
      altitude: 'Altitude',
      satellites: 'Satellites',
      noData: 'No GPS Data',
      failed: 'Failed to load GPS location',
      refresh: 'Refresh Location',
      loading: 'Loading...',
      updateTime: 'Update Time',
      device: 'Device',
    },

    // Images
    images: {
      title: 'Latest Images',
      rgb: 'RGB Image',
      thermal: 'Thermal Image',
      capturedAt: 'Captured At',
      fileSize: 'File Size',
      clickToEnlarge: 'Click to Enlarge',
      noImage: 'No image data available',
      hint: 'Device uploads images every 10 minutes',
      downloadLatest: 'Download Latest',
      batchDownload: 'Batch Download',
      batchDownloadTitle: 'Batch Download Images',
      startTime: 'Start Time',
      endTime: 'End Time',
      downloadZip: 'Download ZIP',
      downloadHint: 'Download selected file types in the time range as a ZIP file',
      npzNotAvailable: 'NPY raw temperature data not yet generated',
      selectAtLeastOneType: 'Please select at least one file type',
      selectDateRange: 'Please select a date range',
      invalidDateRange: 'Start date cannot be later than end date',
      downloading: 'Downloading...',
      downloadSuccess: 'Batch download successful',
      downloadFailed: 'Download failed',
      loading: 'Loading...',
      refresh: 'Refresh',
      close: '✕',
      cancel: 'Cancel',
      confirm: 'Confirm',
      staticDemo: 'Demo Image (Static)',
      demoImage: 'Demo Image',
      npyRaw: 'NPY (raw temperature)',
    },

    // Camera Control
    camera: {
      title: 'Camera Status',
      online: 'Online',
      offline: 'Offline',
      lastCapture: 'Last Capture',
      storage: 'Storage',
      free: 'free',
      captureNow: 'Capture Now',
      capturing: 'Capturing...',
      captureFailed: 'Capture failed',
      loading: 'Loading camera status...',
      noData: 'No camera data available',
      offlineHint: 'Camera offline, capture unavailable',
    },

    // Data Download
    dataDownload: {
      title: 'Download Data',
      csvExport: 'Export CSV',
      selectRange: 'Select Time Range',
      downloading: 'Downloading...',
      success: 'Download successful',
      failed: 'Download failed',
    },

    // Factor Config
    factorConfig: {
      title: 'Factor Settings (Efficiency Correction)',
      factorA: 'Factor A (PA Correction)',
      factorP: 'Factor P (PP Correction)',
      updating: 'Updating...',
      updateButton: 'Update Factor',
      resetButton: 'Reset to 1.0',
      updateSuccess: 'Factor updated successfully',
      updateFailed: 'Factor update failed',
      info: 'Factor is used to correct power data efficiency calculations',
      formulaPGA: 'Formula: PGA% = (PA × Factor_A - PG) / PG × 100',
      formulaPGP: 'Formula: PGP% = (PP × Factor_P - PG) / PG × 100',
    },

    // Charts
    charts: {
      powerTrend: 'Power Trend Chart',
      pagTrend: 'PGA Efficiency Trend (%)',
      pgpTrend: 'PGP Efficiency Trend (%)',
      weatherTrend: 'Weather Data Trend',
      panelTempTrend: 'Panel Temperature Trend (Thermal)',
      panelTemp: 'Panel Temp',
      skyTemp: 'Sky Temp',
      virtualIrradianceTrend: 'Irradiance (Open-Meteo)',
      viEstimated: 'Image estimate (experimental)',
      viCalibrating: 'Image estimate calibrating (needs 20 paired daytime hours) — showing Open-Meteo only',
      rainVisionTrend: 'Rainfall (Open-Meteo + on-site wet confirm)',
      rainScore: 'On-site wet confirmed',
      rainMm: 'Open-Meteo rainfall',
      soilingVisionTrend: 'Surface Contamination (image) — clear-sky frames, baseline auto-resets after rain',
      roiCalib: 'ROI Calibration',
      soilingDiff: 'Anti-soiling benefit (REF−coated)',
      brightness: 'Soiling index',
      timeRangeOptions: {
        '1': '1 min/point (Last 60 min)',
        '5': '5 min/point (Last 5 hours)',
        '10': '10 min/point (Last 10 hours)',
        '30': '30 min/point (Last 30 hours)',
        '60': '1 hour/point (Last 60 hours)',
        '360': '6 hours/point (Last 15 days)',
        '1440': '1 day/point (Last 60 days)',
      },
      efficiencyTimeRange: {
        '60': 'Last 1 Hour',
        '180': 'Last 3 Hours',
        '360': 'Last 6 Hours',
        '720': 'Last 12 Hours',
        '1440': 'Last 1 Day',
        '4320': 'Last 3 Days',
        '10080': 'Last 1 Week',
      },
      downloadButton: 'Download Data (CSV)',
      downloadWeather: 'Download Weather (CSV)',
      downloadCsv: 'Download CSV',
      energyTrend: 'Total Energy (kWh per interval, from 10-min averages)',
      energyPg: 'PG energy',
      energyPa: 'PA energy',
      energyPp: 'PP energy',
      energyFilled: 'interpolated 10-min buckets',
      energyIntervalOptions: {
        '10': '10 min/point (Last 10 hours)',
        '30': '30 min/point (Last 30 hours)',
        '60': '1 hour/point (Last 60 hours)',
        '360': '6 hours/point (Last 15 days)',
        '1440': '1 day/point (Last 60 days)',
      },
      gainTrendAp: 'Total Energy Gain — AP vs REF (%)',
      gainTrendAr: 'Total Energy Gain — AR vs REF (%)',
      gainAp: 'AP vs REF',
      gainAr: 'AR vs REF',
      gainAxis: 'gain %',
      rearRefTrend: 'Rear Reference Ratio — PP / PG (%, rises as the front panel soils)',
      rearRefRatio: 'PP / PG',
      rearRefAxis: 'PP / PG %',
      soilingPowerTrend: 'Electrical Soiling — front-panel loss vs rear reference',
      soilingPowerMeta: 'Daily 10:00–14:00 PG/PP, PA/PP · post-rain peak ratio = 0% · dashed = image soiling',
      lossPgPp: 'PG/PP energy loss % (REF panel)',
      lossPaPp: 'PA/PP energy loss % (AR panel)',
      lossAxis: 'energy loss %',
      soilIndexAxis: 'image soiling index',
      apSoil: 'AP soiling',
      arSoil: 'AR soiling',
      refSoil: 'REF soiling',
      soilAxis: 'Soiling (R-B vs own baseline)',
    },

    // CSV Download
    csvDownload: {
      title: 'Download Power Data (CSV)',
      weatherTitle: 'Download Weather Data (CSV)',
      startDate: 'Start Date',
      endDate: 'End Date',
      hint: 'Download all raw data (unaggregated) within the specified time range',
      weatherHint: 'Download all raw weather data (solar radiation, rainfall, wind power) within the specified time range',
      cancel: 'Cancel',
      confirm: 'Confirm Download',
      downloading: 'Downloading...',
      success: 'Download successful',
      failed: 'Download failed',
      selectDates: 'Please select start and end dates',
      invalidRange: 'Start date must be earlier than end date',
      rangeTooLong: 'Time range cannot exceed 90 days',
      noData: 'No data available for download',
      successCount: 'CSV downloaded successfully — {count} records',
    },

    // Weather Data
    weather: {
      solarRadiation: 'Solar Radiation',
      rainfall: 'Rainfall Monitor',
      windPower: 'Wind Power',
      noData: 'No Data',
      // Solar radiation levels
      radiationLow: 'Low Radiation',
      radiationMedium: 'Moderate Radiation',
      radiationHigh: 'High Radiation',
      radiationVeryHigh: 'Extreme Radiation',
      // Rainfall
      totalRainfall: 'Total Rainfall',
      rainfallRate: 'Rainfall Rate',
      rainDry: 'No Rain',
      rainLight: 'Light Rain',
      rainModerate: 'Moderate Rain',
      rainHeavy: 'Heavy Rain',
      rainTorrential: 'Torrential Rain',
      // Wind
      windStopped: 'Stopped',
      windLow: 'Low Wind',
      windMedium: 'Normal Generation',
      windHigh: 'High Efficiency',
      windVeryHigh: 'Full Capacity',
      generating: 'Generating',
      standby: 'Standby',
    },
  },

  devices: {
    title: 'Select Monitoring Device',
    subtitle: 'Please select a solar power device to monitor',
    selectDevice: 'Please select a device',
    noDevices: 'No devices available',
    noDevicesHint: 'Please contact administrator to add devices',
    viewDashboard: 'View Dashboard',
    loading: 'Loading device list...',
    loadFailed: 'Failed to load device list',
    retry: 'Retry',
    device: 'Device',
    deviceType: 'Device Type',
    lastSeen: 'Last Seen',
    updatedAt: 'Updated at',
    noData: 'No Data',
    online: 'Online',
    offline: 'Offline',
    solarSystem: 'Solar Power System',
  },

  settings: {
    title: 'System Settings',
    language: 'Language Settings',
    theme: 'Theme Settings',
    notifications: 'Notification Settings',
  },

  errors: {
    network: 'Network connection failed',
    unauthorized: 'Unauthorized, please login again',
    forbidden: 'No permission to access this device',
    notFound: 'Resource not found',
    serverError: 'Server error',
    unknownError: 'Unknown error',
    loadFailed: 'Load failed',
    adminOnly: 'You do not have permission to access the admin page',
  },

  coating: {
    title: 'Coating Performance',
    navButton: '🧪 Coating',
    subtitle: 'AP (anti-soiling) / AR (anti-reflective) / REF (uncoated) panel comparison',
    apGain: 'AP gain vs REF',
    arGain: 'AR gain vs REF',
    soilingAmplitude: 'Current soiling amplitude',
    cleaningIn: 'Cleaning recommended',
    cleaningDays: 'in {days} days',
    cleaningNow: 'Clean now',
    cleaningNone: 'No accumulation trend',
    lifespan: 'Film lifespan trend',
    lifespanDegrading: 'Slowly degrading',
    lifespanStable: 'No degradation',
    lifespanInsufficient: 'Collecting data',
    prCurrent: 'Current PR (REF uncoated)',
    prChart: 'Daily PR vs clean state (GPS + Open-Meteo irradiance, dashed = 80% cleaning threshold)',
    prThresholdNote: 'Cleaning recommended below PR 80%',
    prUnavailable: 'Irradiance data unavailable',
    valleyPoints: 'Valley (cycle minimum)',
    valleyLabel: 'Film intrinsic (valley)',
    logCleaning: 'Log cleaning',
    logCleaningConfirm: 'Log a manual cleaning event (now)? Future analysis will label this day as a manual clean.',
    baselineChart: 'Daily coating gain vs REF + trends (▼ blue=rain orange=logged clean grey=unknown; ◆ white=valley)',
    year: 'yr',
    yearsLeft: 'yr to zero',
    dataRange: 'Data range',
    days: 'days',
    resetEvents: 'Reset events',
    loading: 'Computing analysis...',
  },

  messages: {
    saveSuccess: 'Saved successfully',
    saveFailed: 'Save failed',
    deleteSuccess: 'Deleted successfully',
    deleteFailed: 'Delete failed',
    updateSuccess: 'Updated successfully',
    updateFailed: 'Update failed',
    confirmDelete: 'Are you sure you want to delete?',
    unsavedChanges: 'You have unsaved changes',
  },

  titles: {
    login: 'SolarSDGs IoT - Login',
    devices: 'SolarSDGs IoT - Select Device',
    dashboard: 'SolarSDGs IoT - Live Monitoring',
    coating: 'SolarSDGs IoT - Coating Performance',
    roi: 'SolarSDGs IoT - ROI Calibration',
    admin: 'SolarSDGs IoT - Administration',
  },

  admin: {
    title: 'System Administration',
    tabDevices: '📡 Devices',
    tabUsers: '👥 Users',
    deviceList: 'Device List',
    addDevice: '➕ Add Device',
    deviceId: 'Device ID',
    deviceName: 'Device Name',
    status: 'Status',
    createdAt: 'Created At',
    actions: 'Actions',
    enabled: 'Enabled',
    disabled: 'Disabled',
    enable: 'enable',
    disable: 'disable',
    userList: 'User List',
    addUser: '➕ Add User',
    account: 'Account',
    name: 'Name',
    accessibleDevices: 'Accessible Devices',
    allDevicesAdmin: 'All devices (admin)',
    assignDevicesTip: 'Assign devices',
    editDevice: 'Edit Device',
    newDevice: 'Add Device',
    deviceIdPlaceholder: 'e.g. 6003',
    deviceNamePlaceholder: 'e.g. Rooftop solar array A',
    enableDevice: 'Enable device',
    editUser: 'Edit User',
    newUser: 'Add User',
    accountPlaceholder: 'e.g. user01',
    namePlaceholder: 'e.g. John Doe',
    password: 'Password',
    passwordPlaceholder: 'Enter password',
    adminAccount: 'Administrator account (access to all devices)',
    enableAccount: 'Enable account',
    assignTitle: 'Assign Devices',
    setAdmin: 'Set as administrator (access to all devices)',
    unnamedDevice: 'Unnamed device',
    deviceUpdated: 'Device updated successfully',
    deviceCreated: 'Device created successfully',
    userUpdated: 'User updated successfully',
    userCreated: 'User created successfully',
    assignSuccess: 'Devices assigned successfully',
    opFailed: 'Operation failed: {msg}',
    opFailedRetry: 'Operation failed, please try again later',
    confirmToggleDevice: 'Are you sure you want to {action} device {id}?',
    confirmToggleUser: 'Are you sure you want to {action} user {id}?',
    confirmLogout: 'Are you sure you want to log out?',
  },

  roi: {
    title: '3-Panel ROI Calibration',
    back: '← Dashboard',
    apLabel: 'AP (left · anti-soiling hydrophobic)',
    arLabel: 'AR (middle · anti-reflective hydrophilic)',
    refLabel: 'REF (right · uncoated)',
    dupChannel: '⚠️ Duplicate channel assignment — each panel needs a distinct channel',
    saveFailed: 'Save failed, please retry',
    loadImageFailed: 'Failed to load latest image',
    clickHint: 'Click the corners of {label} (3–8 points, 4–5 recommended, right along the panel frame) — {n} points so far',
    allDone: '✅ Panels outlined — confirm the channel mapping below and press Save; the Pi applies it on its next capture (≤10 min)',
    needTwo: '⚠️ Outline at least two panels to save (use “Skip” for a panel not in view)',
    saved: '✅ Saved! Takes effect from the Pi\'s next capture.',
    undo: '↩ Undo',
    reset: '🗑 Reset',
    saving: 'Saving...',
    save: '💾 Save',
    finishPanel: '✔ Finish this panel ({label})',
    skipPanel: '⏭ Not in view, skip ({label})',
    jsonSummary: 'Coordinate JSON (1920×1080) + channel mapping',
  },
}
